import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  crmService,
  Client,
  ContactFollowUpReminder,
  CrmLead,
  CrmLeadWorkflowStatus,
  LeadLifecycleStatus,
  normalizeCrmLeadStatus,
  isoToDatetimeLocalValue,
  datetimeLocalToIso,
} from '../services/crmService';
import {
  leadUpsellService,
  LeadUpsellOpportunity,
  LeadUpsellWritePayload,
  LeadUpsellClientSummary,
  LeadUpsellStatus,
  LeadUpsellType,
} from '../services/leadUpsellService';
import { useAuth } from '../contexts/AuthContext';
import EmailComposeModal from '../components/EmailComposeModal';
import EnrollSequenceModal from '../components/sequences/EnrollSequenceModal';
import BulkEnrollSequenceModal from '../components/sequences/BulkEnrollSequenceModal';
import EmailSuggestionModal from '../components/EmailSuggestionModal';
import OutreachCampaignModal from '../components/OutreachCampaignModal';
import ContactEmailHistory from '../components/ContactEmailHistory';
import { OutboundEmailRow } from '../services/emailService';
import { leadSourceBadgeClass } from '../utils/statusStyles';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import { ListToolbar } from '../components/crm/ListToolbar';
import { DataListTable, Column } from '../components/crm/DataListTable';
import { CrmEmptyState } from '../components/crm/EmptyState';
import { LeadStatusBadge } from '../components/crm/LeadStatusBadge';
import { RowActionsMenu } from '../components/crm/RowActionsMenu';
import { LEAD_WORKFLOW_STATUS_OPTIONS } from '../components/crm/constants';
import { CrmGridSkeleton } from '../components/crm/CrmGridSkeleton';
import { ViewLayoutToggle } from '../components/crm/ViewLayoutToggle';
import CsvImportWizard from '../components/crm/CsvImportWizard';
import CollapsibleFieldHints from '../components/crm/CollapsibleFieldHints';
import { ListPageHeader } from '../components/crm/ListPageHeader';
import AuthenticatedCaptureImage from '../components/crm/AuthenticatedCaptureImage';
import ContactOutreachStatus from '../components/crm/ContactOutreachStatus';
import QuickCaptureFab from '../components/crm/QuickCaptureFab';
import { DncBadge, dncRowClassName } from '../components/crm/DncBadge';
import ScoreBreakdown from '../components/ScoreBreakdown';
import { LifecycleStatusBadge } from '../components/crm/LifecycleStatusBadge';
import ContactFormModal, { ContactFormValues } from '../components/crm/ContactFormModal';
import { displayEmail } from '../utils/placeholderEmail';
import {
  defaultFollowUpDatetimeLocal,
  defaultFollowUpIso,
  followUpDatetimeLocalOrDefault,
} from '../utils/followUpDefaults';
import {
  nextBestActionForInboundLead,
  nextBestActionForPipelineClient,
} from '../utils/nextBestLeadAction';
import { getWebsiteDisplayText, getWebsiteLinkHref, sanitizeWebsiteUrlForSave } from '../utils/websiteUrl';

/** Query-param filters from dashboard / attention deep links (API allows combining these). */
type PipelineLinkExtras = {
  follow_up?: 'today' | 'overdue';
  temperature?: 'hot' | 'warm' | 'cold';
  workflow_status?: string;
  /** Demos scheduled this week (backend `demo_week=1`). */
  demo_week?: boolean;
  /** Needs review (quick capture, etc.). */
  requires_review?: boolean;
};

/** Badge + status dropdown for list and grid (readable at-a-glance). */
function LeadWorkflowStatusPicker({
  value,
  disabled,
  converted,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  converted?: boolean;
  onChange: (next: string) => void;
}) {
  const v = normalizeCrmLeadStatus(value);
  return (
    <div className="flex min-w-[11rem] max-w-[16rem] flex-col gap-1.5">
      <LeadStatusBadge status={v} converted={converted} />
      <select
        data-row-action
        disabled={disabled}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="input-field w-full rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg disabled:opacity-50"
      >
        {LEAD_WORKFLOW_STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewTab = (searchParams.get('tab') === 'inbound' ? 'inbound' : 'pipeline') as 'pipeline' | 'inbound';
  const viewToken = searchParams.get('view');
  const leadsLayout = searchParams.get('layout') === 'grid' ? 'grid' : 'list';
  const setLeadsLayout = (mode: 'list' | 'grid') => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (mode === 'grid') n.set('layout', 'grid');
        else n.delete('layout');
        return n;
      },
      { replace: true }
    );
  };
  const setViewTab = (t: 'pipeline' | 'inbound') => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set('tab', t);
        return n;
      },
      { replace: true }
    );
  };

  const [leads, setLeads] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showCsvImportWizard, setShowCsvImportWizard] = useState(false);
  const [showImportEntryModal, setShowImportEntryModal] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEmailSuggestionModal, setShowEmailSuggestionModal] = useState(false);
  const [editScheduledEmail, setEditScheduledEmail] = useState<OutboundEmailRow | null>(null);
  const [emailContact, setEmailContact] = useState<Client | CrmLead | null>(null);
  const [emailModalKind, setEmailModalKind] = useState<'client' | 'lead'>('client');
  const [enrollContact, setEnrollContact] = useState<{ type: 'client' | 'lead'; id: string; name: string } | null>(null);
  const [bulkEnrollContacts, setBulkEnrollContacts] = useState<{ type: 'client' | 'lead'; id: string }[] | null>(null);
  const [emailRefreshKey, setEmailRefreshKey] = useState(0);
  /** Quick Capture modal — controlled for FAB + `/leads?quickCapture=true` deep link */
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [inboundLeads, setInboundLeads] = useState<CrmLead[]>([]);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [inboundCount, setInboundCount] = useState(0);
  const [inboundPage, setInboundPage] = useState(1);
  const [pipelinePage, setPipelinePage] = useState(1);
  const [selectedLead, setSelectedLead] = useState<Client | null>(null);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState<Set<string>>(new Set());
  const [selectedInboundIds, setSelectedInboundIds] = useState<Set<string>>(new Set());
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [pipelineSearch, setPipelineSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [pipelineQuickFilter, setPipelineQuickFilter] = useState<
    | ''
    | 'followups_today'
    | 'overdue'
    | 'hot'
    | 'warm'
    | 'cold'
    | 'new_leads'
    | 'closed_won'
    | 'needs_review'
    | 'quick_capture'
  >('');
  const [inboundSearch, setInboundSearch] = useState('');
  const [inboundStatus, setInboundStatus] = useState('');
  const [inboundSource, setInboundSource] = useState('');
  const [inboundOrdering, setInboundOrdering] = useState('-created_at');
  const [pipelineOrdering, setPipelineOrdering] = useState('-created_at');
  const [inboundTemperature, setInboundTemperature] = useState<'' | 'hot' | 'warm' | 'cold'>('');
  const [inboundFollowUpQuick, setInboundFollowUpQuick] = useState<'' | 'today' | 'overdue'>('');
  /** Do Not Contact is a compliance state separate from workflow status (pipeline tab). */
  const [pipelineContactFilter, setPipelineContactFilter] = useState<'all' | 'contactable' | 'do_not_contact'>('all');
  const [pipelineLifecycleFilter, setPipelineLifecycleFilter] = useState<LeadLifecycleStatus | 'all'>('active');
  const [pipelineStatusFilter, setPipelineStatusFilter] = useState<CrmLeadWorkflowStatus | 'all'>('all');
  /** Same compliance filter for inbound marketing leads tab. */
  const [inboundContactFilter, setInboundContactFilter] = useState<'all' | 'contactable' | 'do_not_contact'>('all');
  const [inboundLifecycleFilter, setInboundLifecycleFilter] = useState<LeadLifecycleStatus | 'all'>('active');
  const [pipelineListTotal, setPipelineListTotal] = useState<number | null>(null);
  const [pipelineLinkExtras, setPipelineLinkExtras] = useState<PipelineLinkExtras | null>(null);
  const openedPipelineContactRef = useRef<string | null>(null);
  const prevViewTabRef = useRef<'pipeline' | 'inbound'>(viewTab);

  useEffect(() => {
    if (searchParams.get('import') === 'csv') {
      setShowCsvImportWizard(true);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('import');
          return n;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('import') === 'choose') {
      setShowImportEntryModal(true);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('import');
          return n;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const raw = searchParams.get('add');
    if (raw !== 'full' && raw !== '1') return;
    setShowAddModal(true);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('add');
        n.set('tab', 'pipeline');
        return n;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  /** Deep link: `/leads?quickCapture=true` opens the capture modal, then URL is cleaned (replace). */
  useEffect(() => {
    const raw = searchParams.get('quickCapture');
    if (raw !== 'true' && raw !== '1') return;
    setQuickCaptureOpen(true);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('quickCapture');
        n.set('tab', 'pipeline');
        return n;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  /** Close Quick Capture when user leaves pipeline tab (avoid clearing on initial `?quickCapture` + inbound URL). */
  useEffect(() => {
    const prev = prevViewTabRef.current;
    prevViewTabRef.current = viewTab;
    if (prev === 'pipeline' && viewTab !== 'pipeline') setQuickCaptureOpen(false);
  }, [viewTab]);

  /** Apply sort / pipeline filters from URL (dashboard attention links, bookmarks). */
  useEffect(() => {
    if (searchParams.get('sort') === 'attention') {
      setPipelineOrdering('attention');
    } else {
      const ord = searchParams.get('ordering');
      if (ord) setPipelineOrdering(ord);
    }
    const fu = searchParams.get('follow_up');
    const temp = searchParams.get('temperature');
    const wf = searchParams.get('workflow_status');
    const dm = searchParams.get('demo_week');
    const rr = searchParams.get('requires_review');
    const extras: PipelineLinkExtras = {};
    if (fu === 'today' || fu === 'overdue') extras.follow_up = fu;
    if (temp === 'hot' || temp === 'warm' || temp === 'cold') extras.temperature = temp;
    if (wf) extras.workflow_status = normalizeCrmLeadStatus(wf);
    if (dm === '1') extras.demo_week = true;
    if (rr === '1') extras.requires_review = true;
    if (Object.keys(extras).length > 0) {
      setPipelineLinkExtras(extras);
    } else {
      setPipelineLinkExtras(null);
    }
  }, [searchParams]);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { stage: 'lead' };
      if (pipelineOrdering === 'attention') {
        params.sort = 'attention';
      } else {
        params.ordering = pipelineOrdering;
      }
      if (pipelineSearch) params.search = pipelineSearch;
      if (sourceFilter) params.source = sourceFilter;
      params.lifecycle_status = pipelineLifecycleFilter;
      params.page = String(pipelinePage);

      const ex = pipelineLinkExtras;
      if (ex?.follow_up) {
        params.follow_up = ex.follow_up;
      } else if (pipelineQuickFilter === 'followups_today') {
        params.follow_up = 'today';
      } else if (pipelineQuickFilter === 'overdue') {
        params.follow_up = 'overdue';
      }

      if (ex?.temperature) {
        params.temperature = ex.temperature;
      } else if (pipelineQuickFilter === 'hot') {
        params.temperature = 'hot';
      } else if (pipelineQuickFilter === 'warm') {
        params.temperature = 'warm';
      } else if (pipelineQuickFilter === 'cold') {
        params.temperature = 'cold';
      }

      if (pipelineStatusFilter !== 'all') {
        params.workflow_status = pipelineStatusFilter;
      } else if (ex?.workflow_status) {
        params.workflow_status = ex.workflow_status;
      } else if (pipelineQuickFilter === 'new_leads') {
        params.workflow_status = 'new';
      }

      if (pipelineQuickFilter === 'closed_won') params.closed_won = '1';

      if (ex?.demo_week) params.demo_week = '1';

      if (ex?.requires_review) {
        params.requires_review = '1';
      } else if (pipelineQuickFilter === 'needs_review') {
        params.requires_review = '1';
      }

      if (pipelineQuickFilter === 'quick_capture') {
        params.capture_type = 'quick_capture';
      }

      if (pipelineContactFilter === 'contactable') {
        params.contact_filter = 'contactable';
      } else if (pipelineContactFilter === 'do_not_contact') {
        params.contact_filter = 'do_not_contact';
      }

      if (viewToken) params.view = viewToken;

      const response = await crmService.getClients(params);
      setLeads(response.results);
      setPipelineListTotal(typeof response.count === 'number' ? response.count : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [
    pipelineSearch,
    sourceFilter,
    pipelineOrdering,
    pipelineQuickFilter,
    pipelineLinkExtras,
    pipelinePage,
    pipelineContactFilter,
    pipelineLifecycleFilter,
    pipelineStatusFilter,
    viewToken,
  ]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const loadInboundLeads = useCallback(async () => {
    try {
      setInboundLoading(true);
      const params: Record<string, string> = {
        ordering: inboundOrdering,
        page: String(inboundPage),
      };
      if (inboundSearch) params.search = inboundSearch;
      if (inboundStatus) params.status = inboundStatus;
      if (inboundSource) params.source = inboundSource;
      if (inboundTemperature) params.temperature = inboundTemperature;
      if (inboundFollowUpQuick) params.follow_up = inboundFollowUpQuick;
      if (inboundLifecycleFilter !== 'all') params.lifecycle_status = inboundLifecycleFilter;
      if (inboundContactFilter === 'contactable') {
        params.contact_filter = 'contactable';
      } else if (inboundContactFilter === 'do_not_contact') {
        params.contact_filter = 'do_not_contact';
      }

      if (viewToken) params.view = viewToken;

      const response = await crmService.getMarketingLeads(params);
      setInboundLeads(response.results || []);
      setInboundCount(response.count ?? response.results?.length ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbound leads');
    } finally {
      setInboundLoading(false);
    }
  }, [
    inboundSearch,
    inboundStatus,
    inboundSource,
    inboundOrdering,
    inboundPage,
    inboundTemperature,
    inboundFollowUpQuick,
    inboundContactFilter,
    inboundLifecycleFilter,
    viewToken,
  ]);

  useEffect(() => {
    if (viewTab !== 'inbound') return;
    loadInboundLeads();
  }, [viewTab, loadInboundLeads]);

  /** Deep link ?lead=<uuid> opens the inbound detail route */
  useEffect(() => {
    const lid = searchParams.get('lead');
    if (lid && viewTab === 'inbound') {
      navigate(`/leads/inbound/${lid}`, { replace: true });
    }
  }, [searchParams, viewTab, navigate]);

  const openInboundLead = useCallback(
    (lead: CrmLead) => {
      navigate(`/leads/inbound/${lead.id}`);
    },
    [navigate]
  );

  const inboundSortOptions = useMemo(
    () => [
      { value: '-created_at', label: 'Newest first' },
      { value: 'created_at', label: 'Oldest first' },
      { value: '-score', label: 'Score (high)' },
      { value: 'score', label: 'Score (low)' },
      { value: '-last_contacted', label: 'Last contacted' },
      { value: 'next_follow_up_at', label: 'Next follow-up' },
      { value: 'first_name', label: 'Name A–Z' },
      { value: '-first_name', label: 'Name Z–A' },
    ],
    []
  );

  const pipelineSortOptions = useMemo(
    () => [
      { value: 'attention', label: 'Attention (hot first)' },
      { value: '-created_at', label: 'Newest first' },
      { value: 'created_at', label: 'Oldest first' },
      { value: '-next_follow_up_at', label: 'Next follow-up (soonest)' },
      { value: 'next_follow_up_at', label: 'Next follow-up (latest)' },
      { value: '-last_contacted', label: 'Last contacted (recent)' },
      { value: 'last_contacted', label: 'Last contacted (oldest)' },
      { value: 'lead_workflow_status', label: 'Status A–Z' },
      { value: '-lead_workflow_status', label: 'Status Z–A' },
      { value: 'full_name', label: 'Name A–Z' },
      { value: '-full_name', label: 'Name Z–A' },
    ],
    []
  );

  const handleInboundStatusChange = async (lead: CrmLead, status: string) => {
    try {
      const payload: Parameters<typeof crmService.patchMarketingLead>[1] = { status };
      if (status === 'contacted' || status === 'replied') {
        payload.last_contacted = new Date().toISOString();
      }
      await crmService.patchMarketingLead(lead.id, payload);
      await loadInboundLeads();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleInboundTemperatureChange = async (
    lead: CrmLead,
    lead_temperature: 'cold' | 'warm' | 'hot'
  ) => {
    try {
      await crmService.patchMarketingLead(lead.id, { lead_temperature });
      await loadInboundLeads();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update priority');
    }
  };

  const handleInboundNextFollowUpSave = async (lead: CrmLead, localValue: string) => {
    try {
      const next_follow_up_at = datetimeLocalToIso(localValue);
      await crmService.patchMarketingLead(lead.id, { next_follow_up_at });
      await loadInboundLeads();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update next follow-up');
    }
  };

  const handleConvertInboundToProspect = async (il: CrmLead) => {
    try {
      await crmService.convertMarketingLead(il.id);
      await Promise.all([loadInboundLeads(), loadLeads()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert lead');
    }
  };

  const handleCreateLead = async (leadData: any) => {
    setCreateError('');
    setCreateSubmitting(true);
    try {
      await crmService.createClient({
        ...leadData,
        full_name: leadData.full_name || leadData.company_name || 'New Lead',
        stage: 'lead',
      });
      await loadLeads();
      setShowAddModal(false);
    } catch (err) {
      // Report inside the modal: the page-level banner renders behind the modal backdrop.
      setCreateError(err instanceof Error ? err.message : 'Failed to create lead');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleConvertToProspect = async (leadId: string) => {
    try {
      await crmService.updateClient(leadId, { stage: 'prospect' });
      await loadLeads();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert lead');
    }
  };

  const handleUpdateLead = async (leadId: string, leadData: any) => {
    setEditError('');
    setEditSubmitting(true);
    try {
      const updated = await crmService.updateClient(leadId, { ...leadData, stage: 'lead' });
      await loadLeads();
      setShowEditModal(false);
      if (selectedLead?.id === leadId) {
        setSelectedLead(updated);
      } else {
        setSelectedLead(null);
      }
    } catch (err) {
      // The page-level banner renders behind the modal backdrop, so a failure here
      // used to look like a dead Save button. Report it inside the modal instead.
      setEditError(err instanceof Error ? err.message : 'Failed to update lead');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await crmService.deleteClient(leadId);
        await loadLeads();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete lead');
      }
    }
  };

  const openProfilePanel = (lead: Client) => {
    setSelectedLead(lead);
    setShowProfilePanel(true);
  };

  /** Dashboard / ranked list deep links: `/leads?pipeline_contact=<uuid>` opens the profile drawer. */
  useEffect(() => {
    const pid = searchParams.get('pipeline_contact');
    if (!pid) {
      openedPipelineContactRef.current = null;
      return;
    }
    if (viewTab !== 'pipeline') return;
    if (openedPipelineContactRef.current === pid) return;

    const stripParam = () => {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('pipeline_contact');
          return n;
        },
        { replace: true }
      );
    };

    const fromList = leads.find((l) => l.id === pid);
    if (fromList) {
      openProfilePanel(fromList);
      openedPipelineContactRef.current = pid;
      stripParam();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const c = await crmService.getClient(pid);
        if (cancelled || c.stage !== 'lead') return;
        openProfilePanel(c);
        openedPipelineContactRef.current = pid;
        stripParam();
      } catch {
        /* unknown id */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, viewTab, leads, setSearchParams]);

  const getFollowUpUrgency = (lead: Client): 'overdue' | 'today' | 'future' | 'none' => {
    if (!lead.next_follow_up_at) return 'none';
    const due = new Date(lead.next_follow_up_at);
    const now = new Date();
    const dueDate = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (dueDate < todayDate) return 'overdue';
    if (dueDate === todayDate) return 'today';
    return 'future';
  };

  const isSuppressedForSelection = (record: { do_not_contact?: boolean; lifecycle_status?: string }) => {
    const lifecycle = (record.lifecycle_status || '').toLowerCase();
    return !!record.do_not_contact || lifecycle === 'archived' || lifecycle === 'do_not_contact';
  };

  const canSelectPipeline = (record: Client) =>
    pipelineLifecycleFilter !== 'active' || !isSuppressedForSelection(record);
  const canSelectInbound = (record: CrmLead) =>
    inboundLifecycleFilter !== 'active' || !isSuppressedForSelection(record);

  const selectedPipelineLeads = leads.filter((row) => selectedPipelineIds.has(row.id));
  const selectedInboundLeads = inboundLeads.filter((row) => selectedInboundIds.has(row.id));

  const handlePipelineWorkflowChange = async (contact: Client, lead_workflow_status: string) => {
    try {
      const payload: Partial<Client> = { lead_workflow_status };
      if (lead_workflow_status === 'contacted' || lead_workflow_status === 'replied') {
        payload.last_contacted = new Date().toISOString();
      }
      const updated = await crmService.updateClient(contact.id, payload);
      await loadLeads();
      if (selectedLead?.id === contact.id) {
        setSelectedLead(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handlePipelineTemperatureChange = async (contact: Client, lead_temperature: 'cold' | 'warm' | 'hot') => {
    try {
      const updated = await crmService.updateClient(contact.id, { lead_temperature });
      await loadLeads();
      if (selectedLead?.id === contact.id) {
        setSelectedLead(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update temperature');
    }
  };

  const handlePipelineNextFollowUpSave = async (contact: Client, localValue: string) => {
    try {
      const next_follow_up_at = datetimeLocalToIso(localValue);
      const updated = await crmService.updateClient(contact.id, { next_follow_up_at });
      await loadLeads();
      if (selectedLead?.id === contact.id) {
        setSelectedLead(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update next follow-up');
    }
  };

  const handlePipelineQuickNoteSave = async (contact: Client, note: string) => {
    try {
      const updated = await crmService.updateClient(contact.id, { notes: note });
      await loadLeads();
      if (selectedLead?.id === contact.id) {
        setSelectedLead(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update note');
    }
  };

  const handleMarkFollowedUp = async (contact: Client) => {
    try {
      const updated = await crmService.markClientFollowedUp(contact.id);
      await loadLeads();
      if (selectedLead?.id === contact.id) {
        setSelectedLead(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark follow-up');
    }
  };

  const matchesPipelineLifecycle = useCallback(
    (status?: string | null) => pipelineLifecycleFilter === 'all' || (status || 'active') === pipelineLifecycleFilter,
    [pipelineLifecycleFilter]
  );

  const handlePipelineLifecycleChange = async (
    contact: Client,
    lifecycle_status: LeadLifecycleStatus,
    confirmText?: string
  ) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const payload: Partial<Client> = { lifecycle_status };
      if (lifecycle_status === 'do_not_contact') payload.do_not_contact = true;
      if (contact.lifecycle_status === 'do_not_contact' && lifecycle_status !== 'do_not_contact') {
        payload.do_not_contact = false;
      }
      const updated = await crmService.updateClient(contact.id, payload);
      setLeads((prev) => {
        const next = prev.map((lead) => (lead.id === contact.id ? updated : lead));
        return next.filter((lead) => matchesPipelineLifecycle(lead.lifecycle_status));
      });
      if (selectedLead?.id === contact.id) {
        if (matchesPipelineLifecycle(updated.lifecycle_status)) setSelectedLead(updated);
        else setSelectedLead(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lifecycle status');
    }
  };

  const matchesInboundLifecycle = useCallback(
    (status?: string | null) => inboundLifecycleFilter === 'all' || (status || 'active') === inboundLifecycleFilter,
    [inboundLifecycleFilter]
  );

  const handleInboundLifecycleChange = async (
    lead: CrmLead,
    lifecycle_status: LeadLifecycleStatus,
    confirmText?: string
  ) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const payload: Parameters<typeof crmService.patchMarketingLead>[1] = { lifecycle_status };
      if (lifecycle_status === 'do_not_contact') payload.do_not_contact = true;
      if (lead.lifecycle_status === 'do_not_contact' && lifecycle_status !== 'do_not_contact') {
        payload.do_not_contact = false;
      }
      const updated = await crmService.patchMarketingLead(lead.id, payload);
      setInboundLeads((prev) => {
        const next = prev.map((row) => (row.id === lead.id ? updated : row));
        return next.filter((row) => matchesInboundLifecycle(row.lifecycle_status));
      });
      setInboundCount((prev) => (matchesInboundLifecycle(updated.lifecycle_status) ? prev : Math.max(0, prev - 1)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lifecycle status');
    }
  };

  const togglePipelineSelection = (id: string) => {
    setSelectedPipelineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleInboundSelection = (id: string) => {
    setSelectedInboundIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisiblePipeline = () => {
    setSelectedPipelineIds(
      new Set(leads.filter((row) => canSelectPipeline(row)).map((row) => row.id))
    );
  };

  const selectAllVisibleInbound = () => {
    setSelectedInboundIds(
      new Set(inboundLeads.filter((row) => canSelectInbound(row)).map((row) => row.id))
    );
  };

  const pipelineColumns: Column<Client>[] = [
    {
      id: 'select',
      header: (
        <input
          data-row-action
          type="checkbox"
          aria-label="Select all visible pipeline leads"
          checked={
            leads.filter((row) => canSelectPipeline(row)).length > 0 &&
            leads.filter((row) => canSelectPipeline(row)).every((row) => selectedPipelineIds.has(row.id))
          }
          onChange={(e) => {
            if (e.target.checked) selectAllVisiblePipeline();
            else setSelectedPipelineIds(new Set());
          }}
        />
      ),
      className: 'w-10',
      hideOnMobile: true,
      render: (lead) => (
        <input
          data-row-action
          type="checkbox"
          checked={selectedPipelineIds.has(lead.id)}
          disabled={!canSelectPipeline(lead)}
          onChange={() => togglePipelineSelection(lead.id)}
        />
      ),
    },
    {
      id: 'name',
      header: 'Name',
      mobilePriority: 'primary',
      mobileLabel: 'Name',
      render: (lead) => (
        <div>
          <div className="font-medium text-fg flex items-center gap-2 flex-wrap">
            <span>{lead.full_name || lead.display_name}</span>
            {lead.do_not_contact ? <DncBadge /> : null}
          </div>
          <div className="text-xs text-fg-muted">{displayEmail(lead.email) || 'No email'}</div>
        </div>
      ),
    },
    {
      id: 'compliance',
      header: 'Reach',
      mobileLabel: 'Reach',
      className: 'w-[7rem]',
      cellClassName: 'align-top',
      render: (lead) =>
        lead.do_not_contact ? (
          <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Suppressed</span>
        ) : (
          <span className="text-xs text-fg-muted">Contactable</span>
        ),
    },
    {
      id: 'photo',
      header: '',
      className: 'w-14',
      hideOnMobile: true,
      render: (lead) =>
        lead.capture_type === 'quick_capture' ? (
          <AuthenticatedCaptureImage clientId={lead.id} alt="" className="h-10 w-10 border border-border" />
        ) : null,
    },
    { id: 'company', header: 'Company', mobileLabel: 'Company', render: (lead) => lead.company_name || '—' },
    {
      id: 'website',
      header: 'Website',
      mobileLabel: 'Website',
      render: (lead) => {
        const href = getWebsiteLinkHref(lead.website_url);
        const label = getWebsiteDisplayText(lead.website_url) || '—';
        if (!href) return <span className="text-fg-muted">{label}</span>;
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            {label}
          </a>
        );
      },
    },
    { id: 'phone', header: 'Phone', mobileLabel: 'Phone', render: (lead) => formatPhoneDisplay(lead.phone) },
    {
      id: 'source',
      header: 'Source',
      mobileLabel: 'Source',
      render: (lead) => (
        <div className="flex flex-col gap-1 items-start">
          {lead.source ? (
            <span className={leadSourceBadgeClass(lead.source)}>{lead.source}</span>
          ) : (
            <span className="text-fg-muted">—</span>
          )}
          {lead.capture_type === 'quick_capture' ? (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-primary">Captured</span>
          ) : null}
          {lead.requires_review ? (
            <span className="text-[10px] font-medium text-warning-fg">Needs review</span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'workflow_status',
      header: 'Status',
      className: 'min-w-[13rem]',
      cellClassName: 'whitespace-normal align-top',
      render: (lead) => (
        <LeadWorkflowStatusPicker
          value={lead.lead_workflow_status ?? 'new'}
          onChange={(next) => handlePipelineWorkflowChange(lead, next)}
        />
      ),
    },
    {
      id: 'lifecycle_status',
      header: 'Lifecycle',
      render: (lead) => <LifecycleStatusBadge status={lead.lifecycle_status} />,
    },
    {
      id: 'temperature',
      header: (
        <span title="Hot = contact first; warm = soon; cold = nurture or deprioritize">Priority</span>
      ),
      render: (lead) => (
        <select
          data-row-action
          value={lead.lead_temperature ?? 'warm'}
          onChange={(e) => handlePipelineTemperatureChange(lead, e.target.value as 'cold' | 'warm' | 'hot')}
          className="input-field rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg"
        >
          <option value="cold">Cold</option>
          <option value="warm">Warm</option>
          <option value="hot">Hot</option>
        </select>
      ),
    },
    {
      id: 'score',
      header: (
        <span title="0–100 score from fit, source, and activity (same logic as the server)">Score</span>
      ),
      className: 'w-16',
      render: (lead) => (
        <span
          className="font-medium tabular-nums text-fg"
          title="0–100. Higher usually means stronger fit or more complete profile."
        >
          {lead.lead_score != null ? String(lead.lead_score) : '—'}
        </span>
      ),
    },
    {
      id: 'next_action',
      header: 'Next best action',
      className: 'min-w-[10rem] max-w-[14rem]',
      cellClassName: 'align-top',
      render: (lead) => (
        <p className="text-xs leading-snug text-fg-muted" title={nextBestActionForPipelineClient(lead)}>
          {nextBestActionForPipelineClient(lead)}
        </p>
      ),
    },
    {
      id: 'assigned',
      header: 'Assigned',
      render: (lead) => lead.assigned_to_name || '—',
    },
    {
      id: 'created',
      header: 'Created',
      render: (lead) => new Date(lead.created_at).toLocaleDateString(),
    },
    {
      id: 'last_contacted',
      header: 'Last contacted',
      render: (lead) =>
        lead.last_contacted ? new Date(lead.last_contacted).toLocaleString() : '—',
    },
    {
      id: 'next_follow_up',
      header: 'Next follow-up',
      render: (lead) => (
        <input
          key={`${lead.id}-fu-${lead.next_follow_up_at ?? ''}-${lead.updated_at ?? ''}`}
          data-row-action
          type="datetime-local"
          defaultValue={followUpDatetimeLocalOrDefault(lead.next_follow_up_at)}
          onBlur={(e) => handlePipelineNextFollowUpSave(lead, e.target.value)}
          className="input-field w-full rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg"
        />
      ),
    },
    {
      id: 'quick_note',
      header: 'Notes',
      className: 'min-w-[14rem]',
      render: (lead) => (
        <input
          data-row-action
          type="text"
          defaultValue={lead.notes ?? ''}
          placeholder="Add quick note..."
          onBlur={(e) => handlePipelineQuickNoteSave(lead, e.target.value)}
          className="input-field w-full rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg"
        />
      ),
    },
    {
      id: 'urgency',
      header: 'Urgency',
      render: (lead) => {
        const urgency = getFollowUpUrgency(lead);
        if (urgency === 'overdue') return <span className="text-xs font-semibold text-danger-fg">Overdue</span>;
        if (urgency === 'today') return <span className="text-xs font-semibold text-warning-fg">Today</span>;
        if (urgency === 'future') return <span className="text-xs text-fg-muted">Scheduled</span>;
        return <span className="text-xs text-fg-muted">No date</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      className: 'w-24',
      cellClassName: 'text-right',
      render: (lead) => (
        <RowActionsMenu
          items={[
            { id: 'view', label: 'View', onClick: () => openProfilePanel(lead) },
            {
              id: 'edit',
              label: 'Edit',
              onClick: () => {
                setSelectedLead(lead);
                setEditError('');
                setShowEditModal(true);
              },
            },
            { id: 'dormant', label: 'Move to Dormant', onClick: () => handlePipelineLifecycleChange(lead, 'dormant') },
            {
              id: 'archive',
              label: 'Archive',
              onClick: () => handlePipelineLifecycleChange(lead, 'archived', 'Archive this lead?'),
            },
            {
              id: 'mark_dnc',
              label: 'Mark Do Not Contact',
              onClick: () =>
                handlePipelineLifecycleChange(lead, 'do_not_contact', 'Mark this lead as Do Not Contact?'),
            },
            { id: 'restore', label: 'Restore to Active', onClick: () => handlePipelineLifecycleChange(lead, 'active') },
            { id: 'followed', label: 'Mark Followed Up', onClick: () => handleMarkFollowedUp(lead) },
            {
              id: 'sequence',
              label: 'Start sequence',
              onClick: () =>
                setEnrollContact({ type: 'client', id: lead.id, name: lead.full_name || lead.display_name || lead.email }),
            },
            { id: 'convert', label: 'Convert to prospect', onClick: () => handleConvertToProspect(lead.id) },
            { id: 'del', label: 'Delete', onClick: () => handleDeleteLead(lead.id) },
          ]}
        />
      ),
    },
  ];

  const PAGE_SIZE = 20;
  const pipelineTotalPages = Math.max(1, Math.ceil((pipelineListTotal || 0) / PAGE_SIZE));
  const inboundTotalPages = Math.max(1, Math.ceil((inboundCount || 0) / PAGE_SIZE));

  const inboundColumns: Column<CrmLead>[] = [
    {
      id: 'select',
      header: (
        <input
          data-row-action
          type="checkbox"
          aria-label="Select all visible inbound leads"
          checked={
            inboundLeads.filter((row) => canSelectInbound(row)).length > 0 &&
            inboundLeads.filter((row) => canSelectInbound(row)).every((row) => selectedInboundIds.has(row.id))
          }
          onChange={(e) => {
            if (e.target.checked) selectAllVisibleInbound();
            else setSelectedInboundIds(new Set());
          }}
        />
      ),
      className: 'w-10',
      hideOnMobile: true,
      render: (lead) => (
        <input
          data-row-action
          type="checkbox"
          checked={selectedInboundIds.has(lead.id)}
          disabled={!canSelectInbound(lead)}
          onChange={() => toggleInboundSelection(lead.id)}
        />
      ),
    },
    {
      id: 'name',
      header: 'Name',
      mobilePriority: 'primary',
      mobileLabel: 'Name',
      render: (il) => (
        <div>
          <div className="font-medium text-fg flex items-center gap-2 flex-wrap">
            <span>{il.full_name}</span>
            {il.do_not_contact ? <DncBadge /> : null}
          </div>
          <div className="text-xs text-fg-muted">{il.email}</div>
        </div>
      ),
    },
    {
      id: 'compliance',
      header: 'Reach',
      mobileLabel: 'Reach',
      className: 'w-[7rem]',
      cellClassName: 'align-top',
      render: (il) =>
        il.do_not_contact ? (
          <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Suppressed</span>
        ) : (
          <span className="text-xs text-fg-muted">Contactable</span>
        ),
    },
    { id: 'company', header: 'Company', mobileLabel: 'Company', render: (il) => il.company || '—' },
    { id: 'phone', header: 'Phone', mobileLabel: 'Phone', render: (il) => formatPhoneDisplay(il.phone) },
    {
      id: 'source',
      header: 'Source',
      mobileLabel: 'Source',
      render: (il) =>
        il.source ? <span className={leadSourceBadgeClass(il.source)}>{il.source}</span> : '—',
    },
    {
      id: 'status',
      header: 'Status',
      className: 'min-w-[13rem]',
      cellClassName: 'whitespace-normal align-top',
      render: (il) => (
        <LeadWorkflowStatusPicker
          value={il.status}
          converted={!!il.converted_to_client}
          disabled={!!il.converted_to_client}
          onChange={(next) => handleInboundStatusChange(il, next)}
        />
      ),
    },
    {
      id: 'lifecycle',
      header: 'Lifecycle',
      render: (il) => <LifecycleStatusBadge status={il.lifecycle_status} />,
    },
    {
      id: 'score',
      header: (
        <span title="0–100 score from source, engagement, and fit">Score</span>
      ),
      render: (il) => (
        <span className="tabular-nums" title="0–100">
          {il.score != null ? String(il.score) : '—'}
        </span>
      ),
    },
    {
      id: 'temperature',
      header: 'Priority',
      render: (il) => (
        <select
          data-row-action
          disabled={!!il.converted_to_client}
          value={(il.lead_temperature ?? 'warm').toLowerCase()}
          onChange={(e) =>
            handleInboundTemperatureChange(il, e.target.value as 'cold' | 'warm' | 'hot')
          }
          className="input-field rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg disabled:opacity-50"
        >
          <option value="cold">Cold</option>
          <option value="warm">Warm</option>
          <option value="hot">Hot</option>
        </select>
      ),
    },
    {
      id: 'next_action',
      header: 'Next best action',
      className: 'min-w-[10rem] max-w-[14rem]',
      cellClassName: 'align-top',
      render: (il) => (
        <p className="text-xs leading-snug text-fg-muted" title={nextBestActionForInboundLead(il)}>
          {nextBestActionForInboundLead(il)}
        </p>
      ),
    },
    {
      id: 'assigned',
      header: 'Assigned',
      render: (il) => il.assigned_to_name || '—',
    },
    {
      id: 'created',
      header: 'Created',
      render: (il) => new Date(il.created_at).toLocaleDateString(),
    },
    {
      id: 'last',
      header: 'Last contacted',
      render: (il) => (il.last_contacted ? new Date(il.last_contacted).toLocaleString() : '—'),
    },
    {
      id: 'next',
      header: 'Next follow-up',
      render: (il) => (
        <input
          key={`${il.id}-fu-${il.next_follow_up_at ?? ''}-${il.updated_at ?? ''}`}
          data-row-action
          type="datetime-local"
          disabled={!!il.converted_to_client}
          defaultValue={followUpDatetimeLocalOrDefault(il.next_follow_up_at)}
          onBlur={(e) => handleInboundNextFollowUpSave(il, e.target.value)}
          className="input-field w-full min-w-[10rem] rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg disabled:opacity-50"
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      className: 'w-28',
      cellClassName: 'text-right',
      render: (il) => (
        <RowActionsMenu
          items={[
            { id: 'view', label: 'View', onClick: () => openInboundLead(il) },
            {
              id: 'contacted',
              label: 'Mark contacted',
              onClick: () => handleInboundStatusChange(il, 'contacted'),
              disabled: !!il.converted_to_client,
            },
            {
              id: 'convert',
              label: 'Convert to prospect',
              onClick: () => handleConvertInboundToProspect(il),
              disabled: !!il.converted_to_client,
            },
            { id: 'dormant', label: 'Move to Dormant', onClick: () => handleInboundLifecycleChange(il, 'dormant') },
            {
              id: 'archive',
              label: 'Archive',
              onClick: () => handleInboundLifecycleChange(il, 'archived', 'Archive this lead?'),
            },
            {
              id: 'mark_dnc',
              label: 'Mark Do Not Contact',
              onClick: () => handleInboundLifecycleChange(il, 'do_not_contact', 'Mark this lead as Do Not Contact?'),
            },
            { id: 'restore', label: 'Restore to Active', onClick: () => handleInboundLifecycleChange(il, 'active') },
            {
              id: 'sequence',
              label: 'Start sequence',
              onClick: () => setEnrollContact({ type: 'lead', id: il.id, name: il.full_name || il.email }),
            },
            {
              id: 'email',
              label: 'Send email',
              onClick: () => {
                setEmailModalKind('lead');
                setEmailContact(il);
                setShowEmailModal(true);
              },
            },
            {
              id: 'email_suggestion',
              label: 'Generate email suggestion',
              onClick: () => {
                setEmailModalKind('lead');
                setEmailContact(il);
                setShowEmailSuggestionModal(true);
              },
              disabled: !!il.converted_to_client,
            },
          ]}
        />
      ),
    },
  ];

  const pipelineAdvancedFilterCount = useMemo(() => {
    let n = 0;
    if (pipelineLifecycleFilter !== 'active') n += 1;
    if (pipelineStatusFilter !== 'all') n += 1;
    if (pipelineContactFilter !== 'all') n += 1;
    if (sourceFilter) n += 1;
    return n;
  }, [pipelineLifecycleFilter, pipelineStatusFilter, pipelineContactFilter, sourceFilter]);

  const inboundAdvancedFilterCount = useMemo(() => {
    let n = 0;
    if (inboundLifecycleFilter !== 'active') n += 1;
    if (inboundContactFilter !== 'all') n += 1;
    if (inboundStatus) n += 1;
    if (inboundSource) n += 1;
    if (inboundTemperature) n += 1;
    return n;
  }, [inboundLifecycleFilter, inboundContactFilter, inboundStatus, inboundSource, inboundTemperature]);

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Leads"
        subtitle="Capture and work your pipeline — add leads, run outreach, and follow up."
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="btn-primary px-4 py-2 rounded-xl text-sm font-medium inline-flex items-center"
            >
              Add lead
            </button>
            <Link to="/campaigns" className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium">
              Campaign history
            </Link>
            <Link to="/settings/email" className="btn-secondary px-4 py-2 rounded-xl text-sm font-medium">
              Setup
            </Link>
          </>
        }
      />

      {/* Pipeline vs inbound (marketing) leads + list/grid */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setViewTab('pipeline')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              viewTab === 'pipeline'
                ? 'bg-primary text-primary-fg'
                : 'bg-surface border border-border text-fg-muted hover:text-fg'
            }`}
          >
            Pipeline contacts
          </button>
          <button
            type="button"
            onClick={() => setViewTab('inbound')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              viewTab === 'inbound'
                ? 'bg-primary text-primary-fg'
                : 'bg-surface border border-border text-fg-muted hover:text-fg'
            }`}
          >
            Inbound leads
          </button>
          <Link
            to="/leads/suggestions"
            className="px-4 py-2 rounded-xl text-sm font-medium bg-surface border border-border text-fg-muted hover:text-fg"
          >
            Suggestions
          </Link>
        </div>
        <ViewLayoutToggle mode={leadsLayout} onChange={setLeadsLayout} />
      </div>

      {/* view= filter chip (from dashboard deep-links) */}
      {viewToken ? (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/30 px-3 py-1 text-sm text-primary font-medium">
            {viewToken === 'due_today'
              ? 'Due today'
              : viewToken === 'contactable'
              ? 'Contactable'
              : viewToken === 'new_this_week'
              ? 'New this week'
              : viewToken === 'cohort_30d'
              ? '30-day cohort'
              : viewToken}
            <button
              type="button"
              aria-label="Clear view filter"
              onClick={() =>
                setSearchParams((prev) => {
                  const n = new URLSearchParams(prev);
                  n.delete('view');
                  return n;
                }, { replace: true })
              }
              className="ml-1 hover:text-primary-fg rounded-full"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}

      {/* Error Message */}
      {error && (
        <div className="alert-danger px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      {viewTab === 'pipeline' ? (
        <CollapsibleFieldHints variant="pipeline" />
      ) : (
        <CollapsibleFieldHints variant="inbound" />
      )}

      {viewTab === 'pipeline' && (
        <>
          <ListToolbar
            searchValue={pipelineSearch}
            onSearchChange={(value) => {
              setPipelineSearch(value);
              setPipelinePage(1);
            }}
            searchPlaceholder="Search name, email, company…"
            sortValue={pipelineOrdering}
            sortOptions={pipelineSortOptions}
            onSortChange={(value) => {
              setPipelineOrdering(value);
              setPipelinePage(1);
            }}
            showClear={!!(
              pipelineSearch ||
              sourceFilter ||
              pipelineQuickFilter ||
              pipelineLinkExtras ||
              pipelineStatusFilter !== 'all' ||
              pipelineContactFilter !== 'all' ||
              pipelineLifecycleFilter !== 'active'
            )}
            onClearFilters={() => {
              setPipelineSearch('');
              setSourceFilter('');
              setPipelineQuickFilter('');
              setPipelineLinkExtras(null);
              setPipelineOrdering('-created_at');
              setPipelineStatusFilter('all');
              setPipelineContactFilter('all');
              setPipelineLifecycleFilter('active');
              setPipelinePage(1);
            }}
            quickFilter={
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-fg-muted">Quick filter</label>
                <select
                  value={pipelineQuickFilter}
                  onChange={(e) => {
                    setPipelineLinkExtras(null);
                    setPipelineQuickFilter(
                      e.target.value as
                        | ''
                        | 'followups_today'
                        | 'overdue'
                        | 'hot'
                        | 'warm'
                        | 'cold'
                        | 'new_leads'
                        | 'closed_won'
                        | 'needs_review'
                        | 'quick_capture'
                    );
                    setPipelinePage(1);
                  }}
                  className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                >
                  <option value="">All leads</option>
                  <option value="followups_today">Follow-ups Today</option>
                  <option value="overdue">Overdue Follow-ups</option>
                  <option value="hot">Hot priority</option>
                  <option value="warm">Warm priority</option>
                  <option value="cold">Cold priority</option>
                  <option value="new_leads">New Leads</option>
                  <option value="closed_won">Qualified</option>
                  <option value="needs_review">Needs review</option>
                  <option value="quick_capture">Quick capture</option>
                </select>
              </div>
            }
            advancedFilterCount={pipelineAdvancedFilterCount}
            advancedFilters={
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Status / Lifecycle</label>
                  <select
                    value={pipelineLifecycleFilter}
                    onChange={(e) => {
                      setPipelineLifecycleFilter(e.target.value as LeadLifecycleStatus | 'all');
                      setPipelinePage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[12rem]"
                  >
                    <option value="active">Active</option>
                    <option value="dormant">Dormant</option>
                    <option value="archived">Archived</option>
                    <option value="do_not_contact">Do Not Contact</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Workflow status</label>
                  <select
                    value={pipelineStatusFilter}
                    onChange={(e) => {
                      setPipelineLinkExtras((prev) => {
                        if (!prev?.workflow_status) return prev;
                        const rest = { ...prev };
                        delete rest.workflow_status;
                        return Object.keys(rest).length > 0 ? rest : null;
                      });
                      setPipelineQuickFilter((prev) => (prev === 'new_leads' ? '' : prev));
                      setPipelineStatusFilter(e.target.value as CrmLeadWorkflowStatus | 'all');
                      setPipelinePage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[12rem]"
                  >
                    <option value="all">All workflow statuses</option>
                    {LEAD_WORKFLOW_STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Compliance</label>
                  <select
                    value={pipelineContactFilter}
                    onChange={(e) => {
                      setPipelineContactFilter(e.target.value as 'all' | 'contactable' | 'do_not_contact');
                      setPipelinePage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[11rem]"
                  >
                    <option value="all">All</option>
                    <option value="contactable">Contactable</option>
                    <option value="do_not_contact">Do Not Contact</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Source</label>
                  <select
                    value={sourceFilter}
                    onChange={(e) => {
                      setSourceFilter(e.target.value);
                      setPipelinePage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                  >
                    <option value="">All sources</option>
                    <option value="website">Website</option>
                    <option value="referral">Referral</option>
                    <option value="cold call">Cold Call</option>
                    <option value="social media">Social Media</option>
                    <option value="email">Email</option>
                    <option value="door-to-door">Door-to-Door</option>
                  </select>
                </div>
              </>
            }
          />
          {pipelineLinkExtras && viewTab === 'pipeline' ? (
            <p className="text-xs text-fg-muted -mt-2 mb-2 px-1">
              Filters from a dashboard link are active. Use Clear filters to reset.
            </p>
          ) : null}
          {viewTab === 'pipeline' && pipelineListTotal != null ? (
            <p className="text-xs text-fg-muted -mt-2 mb-2 px-1">
              {pipelineListTotal === 0
                ? 'No matching pipeline contacts.'
                : pipelineContactFilter === 'all'
                  ? `${pipelineListTotal} pipeline contact${pipelineListTotal === 1 ? '' : 's'} · ${
                      leads.filter((l) => !l.do_not_contact).length
                    } contactable on this page`
                  : pipelineContactFilter === 'contactable'
                    ? `${pipelineListTotal} contactable (eligible for email sends from this list)`
                    : `${pipelineListTotal} Do Not Contact (suppressed from outbound email)`}
            </p>
          ) : null}
          {selectedPipelineLeads.length > 0 ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-fg-muted">Selected: {selectedPipelineLeads.length}</span>
            <button type="button" onClick={selectAllVisiblePipeline} className="btn-secondary text-xs">
              Select visible
            </button>
            <button type="button" onClick={() => setSelectedPipelineIds(new Set())} className="btn-secondary text-xs">
              Clear selection
            </button>
            <button
              type="button"
              onClick={() => setBulkEnrollContacts(selectedPipelineLeads.map((l) => ({ type: 'client', id: l.id })))}
              className="btn-secondary text-xs"
            >
              Start sequence
            </button>
            <button
              type="button"
              onClick={() => setShowOutreachModal(true)}
              className="btn-primary text-xs"
            >
              Send Outreach
            </button>
          </div>
          ) : null}
          {leadsLayout === 'list' ? (
            <DataListTable<Client>
              columns={pipelineColumns}
              rows={leads}
              getRowKey={(r) => r.id}
              onRowClick={(lead) => openProfilePanel(lead)}
              getRowClassName={(r) => dncRowClassName(r.do_not_contact)}
              loading={loading}
              skeletonColumns={16}
              emptyContent={
                <CrmEmptyState
                  title="No pipeline contacts yet"
                  description={
                    pipelineLifecycleFilter === 'active'
                      ? 'Snap a photo with Quick Capture (floating button), add a lead, or import a list.'
                      : 'No leads match the selected lifecycle filter.'
                  }
                  secondaryHint="Quick Capture is fastest when you’re away from your desk — details can wait."
                  actionLabel="Add your first lead"
                  onAction={() => setShowAddModal(true)}
                />
              }
            />
          ) : loading ? (
            <CrmGridSkeleton />
          ) : leads.length === 0 ? (
            <CrmEmptyState
              title="No pipeline contacts yet"
              description={
                pipelineLifecycleFilter === 'active'
                  ? 'Snap a photo with Quick Capture (floating button), add a lead, or import a list.'
                  : 'No leads match the selected lifecycle filter.'
              }
              secondaryHint="Quick Capture is fastest when you’re away from your desk — details can wait."
              actionLabel="Add your first lead"
              onAction={() => setShowAddModal(true)}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  className={`rounded-xl border bg-surface p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md ${
                    lead.do_not_contact
                      ? 'border-amber-300/60 dark:border-amber-700/50 bg-amber-50/90 dark:bg-amber-950/25'
                      : getFollowUpUrgency(lead) === 'overdue'
                      ? 'border-danger/40'
                      : getFollowUpUrgency(lead) === 'today'
                      ? 'border-warning/40'
                      : 'border-border'
                  }`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                    openProfilePanel(lead);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                      e.preventDefault();
                      openProfilePanel(lead);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div data-row-action className="mb-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedPipelineIds.has(lead.id)}
                      disabled={!canSelectPipeline(lead)}
                      onChange={() => togglePipelineSelection(lead.id)}
                    />
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-fg flex items-center gap-2 flex-wrap">
                        <span>{lead.full_name || lead.display_name}</span>
                        {lead.do_not_contact ? <DncBadge /> : null}
                      </div>
                      <div className="truncate text-xs text-fg-muted">{displayEmail(lead.email) || 'No email'}</div>
                      {lead.do_not_contact ? (
                        <p className="text-[11px] text-amber-900/90 dark:text-amber-100/90 mt-1">
                          Suppressed from email campaigns
                        </p>
                      ) : null}
                    </div>
                    <div data-row-action className="shrink-0">
                      <RowActionsMenu
                        items={[
                          { id: 'view', label: 'View', onClick: () => openProfilePanel(lead) },
                          {
                            id: 'edit',
                            label: 'Edit',
                            onClick: () => {
                              setSelectedLead(lead);
                              setEditError('');
                              setShowEditModal(true);
                            },
                          },
                          {
                            id: 'dormant',
                            label: 'Move to Dormant',
                            onClick: () => handlePipelineLifecycleChange(lead, 'dormant'),
                          },
                          {
                            id: 'archive',
                            label: 'Archive',
                            onClick: () => handlePipelineLifecycleChange(lead, 'archived', 'Archive this lead?'),
                          },
                          {
                            id: 'mark_dnc',
                            label: 'Mark Do Not Contact',
                            onClick: () =>
                              handlePipelineLifecycleChange(lead, 'do_not_contact', 'Mark this lead as Do Not Contact?'),
                          },
                          { id: 'restore', label: 'Restore to Active', onClick: () => handlePipelineLifecycleChange(lead, 'active') },
                          {
                            id: 'convert',
                            label: 'Convert to prospect',
                            onClick: () => handleConvertToProspect(lead.id),
                          },
                          {
                            id: 'sequence',
                            label: 'Start sequence',
                            onClick: () =>
                              setEnrollContact({ type: 'client', id: lead.id, name: lead.full_name || lead.display_name || lead.email }),
                          },
                          { id: 'followed', label: 'Mark Followed Up', onClick: () => handleMarkFollowedUp(lead) },
                          { id: 'del', label: 'Delete', onClick: () => handleDeleteLead(lead.id) },
                        ]}
                      />
                    </div>
                  </div>
                  <div data-row-action className="mt-3" onClick={(e) => e.stopPropagation()}>
                    <LeadWorkflowStatusPicker
                      value={lead.lead_workflow_status ?? 'new'}
                      onChange={(next) => handlePipelineWorkflowChange(lead, next)}
                    />
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                    <dt className="text-fg-muted">Company</dt>
                    <dd className="text-fg">{lead.company_name || '—'}</dd>
                    <dt className="text-fg-muted">Website</dt>
                    <dd className="text-fg">
                      {(() => {
                        const href = getWebsiteLinkHref(lead.website_url);
                        const label = getWebsiteDisplayText(lead.website_url) || '—';
                        if (!href) return <span className="text-fg-muted">{label}</span>;
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {label}
                          </a>
                        );
                      })()}
                    </dd>
                    {lead.phone ? (
                      <>
                        <dt className="text-fg-muted">Phone</dt>
                        <dd className="text-fg">{formatPhoneDisplay(lead.phone)}</dd>
                      </>
                    ) : null}
                    <dt className="text-fg-muted">Source</dt>
                    <dd className="text-fg">
                      {lead.source ? (
                        <span className={leadSourceBadgeClass(lead.source)}>{lead.source}</span>
                      ) : (
                        '—'
                      )}
                    </dd>
                    <dt className="text-fg-muted">Lifecycle</dt>
                    <dd className="text-fg">
                      <LifecycleStatusBadge status={lead.lifecycle_status} />
                    </dd>
                    <dt className="text-fg-muted">Temperature</dt>
                    <dd className="font-semibold text-fg">{(lead.lead_temperature ?? 'warm').toUpperCase()}</dd>
                    <dt className="text-fg-muted">Score</dt>
                    <dd className="text-fg tabular-nums">
                      {lead.lead_score != null ? String(lead.lead_score) : '—'}
                    </dd>
                    <dt className="text-fg-muted">Assigned</dt>
                    <dd className="text-fg">{lead.assigned_to_name || '—'}</dd>
                    <dt className="text-fg-muted">Last contacted</dt>
                    <dd className="text-fg">
                      {lead.last_contacted ? new Date(lead.last_contacted).toLocaleString() : '—'}
                    </dd>
                    <dt className="text-fg-muted">Next follow-up</dt>
                    <dd className="text-fg">
                      {lead.next_follow_up_at
                        ? new Date(lead.next_follow_up_at).toLocaleString()
                        : '—'}
                    </dd>
                  </dl>
                  <p className="mt-2 text-xs leading-snug text-fg-muted border-t border-border pt-2">
                    {nextBestActionForPipelineClient(lead)}
                  </p>
                </div>
              ))}
            </div>
          )}
          {pipelineTotalPages > 1 ? (
            <div className="flex items-center justify-center gap-4 text-sm text-fg-muted">
              <button
                type="button"
                disabled={pipelinePage <= 1}
                onClick={() => setPipelinePage((p) => Math.max(1, p - 1))}
                className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {pipelinePage} of {pipelineTotalPages}
              </span>
              <button
                type="button"
                disabled={pipelinePage >= pipelineTotalPages}
                onClick={() => setPipelinePage((p) => p + 1)}
                className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}

      {viewTab === 'inbound' && (
        <>
          <ListToolbar
            searchValue={inboundSearch}
            onSearchChange={(v) => {
              setInboundSearch(v);
              setInboundPage(1);
            }}
            searchPlaceholder="Search name, company, email, phone…"
            sortValue={inboundOrdering}
            sortOptions={inboundSortOptions}
            onSortChange={(v) => {
              setInboundOrdering(v);
              setInboundPage(1);
            }}
            showClear={!!(
              inboundSearch ||
              inboundStatus ||
              inboundSource ||
              inboundTemperature ||
              inboundFollowUpQuick ||
              inboundContactFilter !== 'all' ||
              inboundLifecycleFilter !== 'active'
            )}
            onClearFilters={() => {
              setInboundSearch('');
              setInboundStatus('');
              setInboundSource('');
              setInboundTemperature('');
              setInboundFollowUpQuick('');
              setInboundContactFilter('all');
              setInboundLifecycleFilter('active');
              setInboundPage(1);
            }}
            quickFilter={
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-fg-muted">Quick filter</label>
                <select
                  value={inboundFollowUpQuick}
                  onChange={(e) => {
                    setInboundFollowUpQuick(e.target.value as '' | 'today' | 'overdue');
                    setInboundPage(1);
                  }}
                  className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                >
                  <option value="">Any follow-up</option>
                  <option value="today">Due today or earlier</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            }
            advancedFilterCount={inboundAdvancedFilterCount}
            advancedFilters={
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Lifecycle</label>
                  <select
                    value={inboundLifecycleFilter}
                    onChange={(e) => {
                      setInboundLifecycleFilter(e.target.value as LeadLifecycleStatus | 'all');
                      setInboundPage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[12rem]"
                  >
                    <option value="active">Active</option>
                    <option value="dormant">Dormant</option>
                    <option value="archived">Archived</option>
                    <option value="do_not_contact">Do Not Contact</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Compliance</label>
                  <select
                    value={inboundContactFilter}
                    onChange={(e) => {
                      setInboundContactFilter(e.target.value as 'all' | 'contactable' | 'do_not_contact');
                      setInboundPage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg min-w-[11rem]"
                  >
                    <option value="all">All</option>
                    <option value="contactable">Contactable</option>
                    <option value="do_not_contact">Do Not Contact</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Priority</label>
                  <select
                    value={inboundTemperature}
                    onChange={(e) => {
                      setInboundTemperature(e.target.value as '' | 'hot' | 'warm' | 'cold');
                      setInboundPage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                  >
                    <option value="">All</option>
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cold">Cold</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Status</label>
                  <select
                    value={inboundStatus}
                    onChange={(e) => {
                      setInboundStatus(e.target.value);
                      setInboundPage(1);
                    }}
                    className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                  >
                    <option value="">All statuses</option>
                    {LEAD_WORKFLOW_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg-muted">Source</label>
                  <input
                    type="text"
                    value={inboundSource}
                    onChange={(e) => {
                      setInboundSource(e.target.value);
                      setInboundPage(1);
                    }}
                    placeholder="e.g. Direct"
                    className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                  />
                </div>
              </>
            }
          />
          <p className="text-xs text-fg-muted -mt-2 mb-2 px-1">
            {inboundCount === 0
              ? 'No matching inbound leads.'
              : inboundContactFilter === 'all'
                ? `${inboundCount} inbound lead${inboundCount === 1 ? '' : 's'} · ${
                    inboundLeads.filter((l) => !l.do_not_contact).length
                  } contactable on this page`
                : inboundContactFilter === 'contactable'
                  ? `${inboundCount} contactable (eligible for email sends from this list)`
                  : `${inboundCount} Do Not Contact (suppressed from outbound email)`}
          </p>
          {selectedInboundLeads.length > 0 ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-fg-muted">Selected: {selectedInboundLeads.length}</span>
            <button type="button" onClick={selectAllVisibleInbound} className="btn-secondary text-xs">
              Select visible
            </button>
            <button type="button" onClick={() => setSelectedInboundIds(new Set())} className="btn-secondary text-xs">
              Clear selection
            </button>
            <button
              type="button"
              onClick={() => setBulkEnrollContacts(selectedInboundLeads.map((l) => ({ type: 'lead', id: l.id })))}
              className="btn-secondary text-xs"
            >
              Start sequence
            </button>
            <button
              type="button"
              onClick={() => setShowOutreachModal(true)}
              className="btn-primary text-xs"
            >
              Send Outreach
            </button>
          </div>
          ) : null}
          {leadsLayout === 'list' ? (
            <DataListTable<CrmLead>
              columns={inboundColumns}
              rows={inboundLeads}
              getRowKey={(r) => r.id}
              onRowClick={(il) => openInboundLead(il)}
              getRowClassName={(r) => dncRowClassName(r.do_not_contact)}
              loading={inboundLoading}
              skeletonColumns={14}
              emptyContent={
                <CrmEmptyState
                  title="No leads yet"
                  description={
                    inboundLifecycleFilter === 'active'
                      ? 'Add your first lead manually or connect your website to capture inbound leads.'
                      : 'No leads match the selected lifecycle filter.'
                  }
                  secondaryHint="Forms and tracking events will appear here automatically."
                />
              }
            />
          ) : inboundLoading ? (
            <CrmGridSkeleton />
          ) : inboundLeads.length === 0 ? (
            <CrmEmptyState
              title="No leads yet"
              description={
                inboundLifecycleFilter === 'active'
                  ? 'Add your first lead manually or connect your website to capture inbound leads.'
                  : 'No leads match the selected lifecycle filter.'
              }
              secondaryHint="Forms and tracking events will appear here automatically."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {inboundLeads.map((il) => (
                <div
                  key={il.id}
                  className={`rounded-xl border p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md ${
                    il.do_not_contact
                      ? 'border-amber-300/60 dark:border-amber-700/50 bg-amber-50/90 dark:bg-amber-950/25'
                      : 'border-border bg-surface'
                  }`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                    openInboundLead(il);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if ((e.target as HTMLElement).closest('[data-row-action]')) return;
                      e.preventDefault();
                      openInboundLead(il);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div data-row-action className="mb-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedInboundIds.has(il.id)}
                      disabled={!canSelectInbound(il)}
                      onChange={() => toggleInboundSelection(il.id)}
                    />
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-fg flex items-center gap-2 flex-wrap">
                        <span>{il.full_name}</span>
                        {il.do_not_contact ? <DncBadge /> : null}
                      </div>
                      <div className="truncate text-xs text-fg-muted">{il.email}</div>
                      {il.do_not_contact ? (
                        <p className="text-[11px] text-amber-900/90 dark:text-amber-100/90 mt-1">
                          Suppressed from email campaigns
                        </p>
                      ) : null}
                    </div>
                    <div data-row-action className="shrink-0">
                      <RowActionsMenu
                        items={[
                          { id: 'view', label: 'View', onClick: () => openInboundLead(il) },
                          {
                            id: 'contacted',
                            label: 'Mark contacted',
                            onClick: () => handleInboundStatusChange(il, 'contacted'),
                            disabled: !!il.converted_to_client,
                          },
                          {
                            id: 'convert',
                            label: 'Convert to prospect',
                            onClick: () => handleConvertInboundToProspect(il),
                            disabled: !!il.converted_to_client,
                          },
                          { id: 'dormant', label: 'Move to Dormant', onClick: () => handleInboundLifecycleChange(il, 'dormant') },
                          {
                            id: 'archive',
                            label: 'Archive',
                            onClick: () => handleInboundLifecycleChange(il, 'archived', 'Archive this lead?'),
                          },
                          {
                            id: 'mark_dnc',
                            label: 'Mark Do Not Contact',
                            onClick: () =>
                              handleInboundLifecycleChange(il, 'do_not_contact', 'Mark this lead as Do Not Contact?'),
                          },
                          { id: 'restore', label: 'Restore to Active', onClick: () => handleInboundLifecycleChange(il, 'active') },
                          {
                            id: 'sequence',
                            label: 'Start sequence',
                            onClick: () => setEnrollContact({ type: 'lead', id: il.id, name: il.full_name || il.email }),
                          },
                          {
                            id: 'email',
                            label: 'Send email',
                            onClick: () => {
                              setEmailModalKind('lead');
                              setEmailContact(il);
                              setShowEmailModal(true);
                            },
                          },
                          {
                            id: 'email_suggestion',
                            label: 'Generate email suggestion',
                            onClick: () => {
                              setEmailModalKind('lead');
                              setEmailContact(il);
                              setShowEmailSuggestionModal(true);
                            },
                            disabled: !!il.converted_to_client,
                          },
                        ]}
                      />
                    </div>
                  </div>
                  <div data-row-action className="mt-3" onClick={(e) => e.stopPropagation()}>
                    <LeadWorkflowStatusPicker
                      value={il.status}
                      converted={!!il.converted_to_client}
                      disabled={!!il.converted_to_client}
                      onChange={(next) => handleInboundStatusChange(il, next)}
                    />
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                    <dt className="text-fg-muted">Company</dt>
                    <dd className="text-fg">{il.company || '—'}</dd>
                    {il.phone ? (
                      <>
                        <dt className="text-fg-muted">Phone</dt>
                        <dd className="text-fg">{formatPhoneDisplay(il.phone)}</dd>
                      </>
                    ) : null}
                    <dt className="text-fg-muted">Source</dt>
                    <dd className="text-fg">
                      {il.source ? (
                        <span className={leadSourceBadgeClass(il.source)}>{il.source}</span>
                      ) : (
                        '—'
                      )}
                    </dd>
                    <dt className="text-fg-muted">Lifecycle</dt>
                    <dd className="text-fg">
                      <LifecycleStatusBadge status={il.lifecycle_status} />
                    </dd>
                    <dt className="text-fg-muted">Score</dt>
                    <dd className="font-semibold text-primary">{il.score != null ? String(il.score) : '—'}</dd>
                    <dt className="text-fg-muted">Priority</dt>
                    <dd className="text-fg">{(il.lead_temperature ?? 'warm').toUpperCase()}</dd>
                    <dt className="text-fg-muted">Assigned</dt>
                    <dd className="text-fg">{il.assigned_to_name || '—'}</dd>
                    <dt className="text-fg-muted">Created</dt>
                    <dd className="text-fg">{new Date(il.created_at).toLocaleDateString()}</dd>
                    <dt className="text-fg-muted">Last contacted</dt>
                    <dd className="text-fg">
                      {il.last_contacted ? new Date(il.last_contacted).toLocaleString() : '—'}
                    </dd>
                    <dt className="text-fg-muted">Next follow-up</dt>
                    <dd className="text-fg">
                      {il.next_follow_up_at
                        ? new Date(il.next_follow_up_at).toLocaleString()
                        : '—'}
                    </dd>
                  </dl>
                  <div data-row-action className="mt-2 border-t border-border pt-2" onClick={(e) => e.stopPropagation()}>
                    <label className="text-xs text-fg-muted">Priority</label>
                    <select
                      disabled={!!il.converted_to_client}
                      value={(il.lead_temperature ?? 'warm').toLowerCase()}
                      onChange={(e) =>
                        handleInboundTemperatureChange(il, e.target.value as 'cold' | 'warm' | 'hot')
                      }
                      className="input-field mt-1 w-full rounded-lg border border-border bg-input px-2 py-1.5 text-sm text-fg disabled:opacity-50"
                    >
                      <option value="cold">Cold</option>
                      <option value="warm">Warm</option>
                      <option value="hot">Hot</option>
                    </select>
                  </div>
                  <p className="mt-2 text-xs leading-snug text-fg-muted">{nextBestActionForInboundLead(il)}</p>
                </div>
              ))}
            </div>
          )}
          {inboundTotalPages > 1 ? (
            <div className="flex items-center justify-center gap-4 text-sm text-fg-muted">
              <button
                type="button"
                disabled={inboundPage <= 1}
                onClick={() => setInboundPage((p) => Math.max(1, p - 1))}
                className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {inboundPage} of {inboundTotalPages}
              </span>
              <button
                type="button"
                disabled={inboundPage >= inboundTotalPages}
                onClick={() => setInboundPage((p) => p + 1)}
                className="btn-surface-muted rounded-lg px-3 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}

      {/* Add Lead Modal */}
      {showAddModal && (
        <QuickAddLeadModal
          onSave={handleCreateLead}
          onClose={() => {
            setShowAddModal(false);
            setCreateError('');
          }}
          error={createError}
          submitting={createSubmitting}
        />
      )}

      {/* Edit Lead Modal */}
      {showEditModal && selectedLead && (
        <EditLeadModal
          lead={selectedLead}
          error={editError}
          submitting={editSubmitting}
          onSave={(leadData) => handleUpdateLead(selectedLead.id, leadData)}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLead(null);
            setEditError('');
          }}
        />
      )}

      {showCsvImportWizard && (
        <CsvImportWizard
          open={showCsvImportWizard}
          onClose={() => setShowCsvImportWizard(false)}
          defaultObjectType="lead"
          onImported={() => loadLeads()}
        />
      )}

      {showImportEntryModal ? (
        <LeadImportEntryModal
          onClose={() => setShowImportEntryModal(false)}
          onDirectImport={() => {
            setShowImportEntryModal(false);
            setShowCsvImportWizard(true);
          }}
          onStagedImport={() => {
            setShowImportEntryModal(false);
            navigate('/lead-imports');
          }}
        />
      ) : null}

      {/* Profile Panel */}
      {showProfilePanel && selectedLead && (
        <ProfilePanel
          contact={selectedLead}
          onClose={() => {
            setShowProfilePanel(false);
            setShowEmailModal(false);
            setShowEmailSuggestionModal(false);
            setEmailContact(null);
          }}
          onEdit={() => {
            setShowProfilePanel(false);
            setEditError('');
            setShowEditModal(true);
          }}
          onContactUpdated={(c) => setSelectedLead(c)}
          onRefresh={loadLeads}
          onConvert={() => handleConvertToProspect(selectedLead.id)}
          onOpenEmail={() => {
            setEmailModalKind('client');
            setEmailContact(selectedLead);
            setEditScheduledEmail(null);
            setShowEmailModal(true);
          }}
          onEditScheduled={(row) => {
            setEmailModalKind('client');
            setEmailContact(selectedLead);
            setEditScheduledEmail(row);
            setShowEmailModal(true);
          }}
          onOpenEmailSuggestion={() => {
            setEmailModalKind('client');
            setEmailContact(selectedLead);
            setShowEmailSuggestionModal(true);
          }}
          emailRefreshKey={emailRefreshKey}
        />
      )}

      <QuickCaptureFab
        ariaLabel="Photo capture lead"
        open={quickCaptureOpen}
        onOpenChange={setQuickCaptureOpen}
        onCreated={() => {
          setViewTab('pipeline');
          void loadLeads();
        }}
      />

      {showEmailModal && emailContact && (
        <EmailComposeModal
          open={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEditScheduledEmail(null);
            setEmailContact(null);
          }}
          contact={emailContact}
          contactKind={emailModalKind}
          followUpContext="lead"
          editScheduledEmail={editScheduledEmail}
          onSent={() => setEmailRefreshKey((k) => k + 1)}
        />
      )}
      {showEmailSuggestionModal && emailContact && (
        <EmailSuggestionModal
          open={showEmailSuggestionModal}
          onClose={() => setShowEmailSuggestionModal(false)}
          suggestion={null}
          clientId={emailModalKind === 'client' ? emailContact.id : undefined}
          leadId={emailModalKind === 'lead' ? emailContact.id : undefined}
          contextLabel={
            emailModalKind === 'client'
              ? (emailContact as Client).company_name || (emailContact as Client).full_name
              : (emailContact as CrmLead).company || (emailContact as CrmLead).full_name
          }
        />
      )}
      {showOutreachModal ? (
        <OutreachCampaignModal
          open={showOutreachModal}
          onClose={() => setShowOutreachModal(false)}
          selectedClients={viewTab === 'pipeline' ? selectedPipelineLeads : []}
          selectedLeads={viewTab === 'inbound' ? selectedInboundLeads : []}
          onSent={(campaignId) => {
            setSelectedPipelineIds(new Set());
            setSelectedInboundIds(new Set());
            navigate(`/campaigns/${campaignId}`);
          }}
        />
      ) : null}

      {enrollContact && (
        <EnrollSequenceModal contact={enrollContact} onClose={() => setEnrollContact(null)} />
      )}

      {bulkEnrollContacts && (
        <BulkEnrollSequenceModal
          contacts={bulkEnrollContacts}
          onClose={() => setBulkEnrollContacts(null)}
          onDone={() => {
            setSelectedPipelineIds(new Set());
            setSelectedInboundIds(new Set());
          }}
        />
      )}
    </div>
  );
};

interface QuickAddLeadModalProps {
  onSave: (lead: any) => void;
  onClose: () => void;
  error?: string;
  submitting?: boolean;
}

const QuickAddLeadModal: React.FC<QuickAddLeadModalProps> = ({ onSave, onClose, error, submitting }) => (
  <ContactFormModal
    title="Add lead"
    submitLabel="Add lead"
    mode="lead"
    values={{
      full_name: '',
      email: '',
      phone: '',
      company_name: '',
      website_url: '',
      source: 'Direct',
      address: '',
      notes: '',
      stage: 'lead',
      lifecycle_status: 'active',
    }}
    onClose={onClose}
    onSubmit={(payload) => onSave({ ...payload, full_name: payload.full_name || payload.company_name })}
    error={error}
    submitting={submitting}
  />
);

interface EditLeadModalProps {
  lead: Client;
  onSave: (lead: any) => void;
  onClose: () => void;
  error?: string;
  submitting?: boolean;
}

const EditLeadModal: React.FC<EditLeadModalProps> = ({ lead, onSave, onClose, error, submitting }) => (
  <ContactFormModal
    title="Edit lead"
    submitLabel="Save changes"
    mode="lead"
    values={{
      title: lead.title || '',
      full_name: lead.full_name || '',
      // Synthetic capture addresses must edit as empty, not as a fake address.
      email: displayEmail(lead.email),
      phone: lead.phone || '',
      company_name: lead.company_name || '',
      job_title: lead.job_title || '',
      website_url: lead.website_url || '',
      source: lead.source || 'Direct',
      address: lead.address || '',
      notes: lead.notes || '',
      stage: (lead.stage as ContactFormValues['stage']) || 'lead',
      lifecycle_status: (lead.lifecycle_status as LeadLifecycleStatus) || 'active',
      estimated_deal_value: lead.estimated_deal_value ?? '',
      next_follow_up_at: lead.next_follow_up_at ?? null,
      tags: lead.tags || [],
    }}
    onClose={onClose}
    onSubmit={onSave}
    error={error}
    submitting={submitting}
  />
);

interface LeadImportEntryModalProps {
  onClose: () => void;
  onDirectImport: () => void;
  onStagedImport: () => void;
}

const LeadImportEntryModal: React.FC<LeadImportEntryModalProps> = ({ onClose, onDirectImport, onStagedImport }) => (
  <div className="modal-backdrop">
    <div className="relative top-16 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-2xl bg-surface">
      <h3 className="text-lg font-semibold text-fg">Import leads</h3>
      <p className="mt-2 text-sm text-fg-muted">
        Choose a direct CSV import for speed, or staged review when you want to map and validate before commit.
      </p>
      <div className="mt-5 grid gap-3">
        <button type="button" onClick={onDirectImport} className="btn-primary rounded-xl px-4 py-2 text-left">
          Direct CSV import
        </button>
        <button type="button" onClick={onStagedImport} className="btn-secondary rounded-xl px-4 py-2 text-left">
          Staged import review
        </button>
      </div>
      <div className="mt-6 flex justify-end">
        <button type="button" onClick={onClose} className="btn-surface-muted rounded-xl px-4 py-2">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const LEAD_UPSELL_TYPES: { value: LeadUpsellType; label: string }[] = [
  { value: 'add_on_service', label: 'Add-on service' },
  { value: 'premium_upgrade', label: 'Premium upgrade' },
  { value: 'expanded_scope', label: 'Expanded scope' },
  { value: 'additional_location', label: 'Additional location' },
  { value: 'extra_users', label: 'Extra users' },
  { value: 'custom', label: 'Custom' },
];

const LEAD_UPSELL_STATUSES: { value: LeadUpsellStatus; label: string }[] = [
  { value: 'identified', label: 'Identified' },
  { value: 'proposed', label: 'Proposed' },
  { value: 'interested', label: 'Interested' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

function formatLeadUpsellUsd(amount: string | number | undefined | null): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : Number(amount ?? 0);
  if (Number.isNaN(n)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

// Profile Panel Component
interface ProfilePanelProps {
  contact: Client;
  onClose: () => void;
  onEdit: () => void;
  onContactUpdated: (client: Client) => void;
  onRefresh: () => void | Promise<void>;
  onConvert: () => void;
  onOpenEmail: () => void;
  onOpenEmailSuggestion: () => void;
  onEditScheduled: (row: OutboundEmailRow) => void;
  emailRefreshKey: number;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({
  contact,
  onClose,
  onEdit,
  onContactUpdated,
  onRefresh,
  onConvert,
  onOpenEmail,
  onOpenEmailSuggestion,
  onEditScheduled,
  emailRefreshKey,
}) => {
  const { user } = useAuth();
  const canEditLeadUpsells = user?.role === 'admin' || user?.role === 'sales';

  const onContactUpdatedRef = useRef(onContactUpdated);
  onContactUpdatedRef.current = onContactUpdated;
  useEffect(() => {
    let cancelled = false;
    void crmService.getClient(contact.id).then((c) => {
      if (!cancelled) onContactUpdatedRef.current(c);
    });
    return () => {
      cancelled = true;
    };
  }, [contact.id]);

  const [nextFollowUpLocal, setNextFollowUpLocal] = useState(() =>
    followUpDatetimeLocalOrDefault(contact.next_follow_up_at)
  );
  const [emailHistoryCount, setEmailHistoryCount] = useState<number | null>(null);
  const [summary, setSummary] = useState<LeadUpsellClientSummary | null>(null);
  const [upsells, setUpsells] = useState<LeadUpsellOpportunity[]>([]);
  const [loadingUpsells, setLoadingUpsells] = useState(true);
  const [upsellError, setUpsellError] = useState('');
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [editingUpsell, setEditingUpsell] = useState<LeadUpsellOpportunity | null>(null);
  const [actionError, setActionError] = useState('');
  const [outreachMessage, setOutreachMessage] = useState('');
  const [contactReminders, setContactReminders] = useState<ContactFollowUpReminder[]>([]);
  const [reminderAtDraft, setReminderAtDraft] = useState(() => defaultFollowUpDatetimeLocal());
  const [reminderNoteDraft, setReminderNoteDraft] = useState('');
  const [reminderBusy, setReminderBusy] = useState(false);

  const loadContactReminders = useCallback(async () => {
    try {
      const res = await crmService.listContactReminders({ client_id: contact.id, status: 'pending' });
      setContactReminders(res.results ?? []);
    } catch {
      setContactReminders([]);
    }
  }, [contact.id]);

  useEffect(() => {
    setNextFollowUpLocal(followUpDatetimeLocalOrDefault(contact.next_follow_up_at));
  }, [contact.id, contact.next_follow_up_at]);

  useEffect(() => {
    loadContactReminders();
  }, [loadContactReminders]);

  useEffect(() => {
    setEmailHistoryCount(null);
  }, [contact.id]);

  const loadUpsells = useCallback(async () => {
    try {
      setLoadingUpsells(true);
      setUpsellError('');
      const [sum, list] = await Promise.all([
        leadUpsellService.getSummary(contact.id),
        leadUpsellService.listForClient(contact.id),
      ]);
      setSummary(sum);
      setUpsells(list.results);
    } catch (e) {
      setUpsellError(e instanceof Error ? e.message : 'Failed to load upsells');
      setSummary(null);
      setUpsells([]);
    } finally {
      setLoadingUpsells(false);
    }
  }, [contact.id]);

  useEffect(() => {
    loadUpsells();
  }, [loadUpsells]);

  const handleArchiveUpsell = async (u: LeadUpsellOpportunity) => {
    if (!window.confirm(`Archive “${u.title}”?`)) return;
    try {
      await leadUpsellService.archive(contact.id, u.id);
      await loadUpsells();
    } catch (e) {
      setUpsellError(e instanceof Error ? e.message : 'Failed to archive');
    }
  };

  const handleTouchAction = async (channel: 'call' | 'email' | 'text') => {
    try {
      setActionError('');
      const updated = await crmService.logClientTouch(contact.id, { channel });
      onContactUpdated(updated);
      await onRefresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to log activity');
    }
  };

  const handleScheduleFollowUp = async () => {
    try {
      setActionError('');
      const updated = await crmService.updateClient(contact.id, {
        next_follow_up_at: defaultFollowUpIso(),
      });
      setNextFollowUpLocal(isoToDatetimeLocalValue(updated.next_follow_up_at));
      onContactUpdated(updated);
      await onRefresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to schedule follow-up');
    }
  };

  const handleSaveContactReminder = async () => {
    if (!reminderAtDraft.trim()) return;
    try {
      setReminderBusy(true);
      setActionError('');
      const d = new Date(reminderAtDraft);
      if (Number.isNaN(d.getTime())) {
        setActionError('Invalid reminder time');
        return;
      }
      await crmService.createContactReminder({
        client_id: contact.id,
        remind_at: d.toISOString(),
        note: reminderNoteDraft.trim(),
      });
      setReminderAtDraft(defaultFollowUpDatetimeLocal());
      setReminderNoteDraft('');
      await loadContactReminders();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to save reminder');
    } finally {
      setReminderBusy(false);
    }
  };

  const handleDismissReminder = async (id: string) => {
    try {
      setActionError('');
      await crmService.patchContactReminder(id, { status: 'dismissed' });
      await loadContactReminders();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update reminder');
    }
  };

  const handleProfileNextFollowUpBlur = async () => {
    try {
      setActionError('');
      const next_follow_up_at = datetimeLocalToIso(nextFollowUpLocal);
      const updated = await crmService.updateClient(contact.id, { next_follow_up_at });
      setNextFollowUpLocal(isoToDatetimeLocalValue(updated.next_follow_up_at));
      onContactUpdated(updated);
      await onRefresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update next follow-up');
    }
  };

  const handleAddNote = async () => {
    const note = window.prompt('Add a note');
    if (!note?.trim()) return;
    try {
      setActionError('');
      const existing = contact.notes?.trim();
      const merged = existing ? `${existing}\n\n${note.trim()}` : note.trim();
      const updated = await crmService.updateClient(contact.id, { notes: merged });
      onContactUpdated(updated);
      await onRefresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to add note');
    }
  };

  const handleGenerateOutreach = async () => {
    try {
      setActionError('');
      const { message } = await crmService.generateOutreachMessage({
        business_name: contact.company_name || contact.full_name || 'there',
        notes: contact.notes || '',
      });
      setOutreachMessage(message);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to generate outreach message');
    }
  };

  return (
    <div className="modal-backdrop overflow-y-auto">
      <div className="relative top-6 mx-auto p-5 border border-border w-full max-w-3xl shadow-lg rounded-2xl bg-surface my-8">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6 gap-3">
            <h3 className="text-lg font-semibold text-fg">Lead Profile</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onEdit}
                className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-muted/80"
              >
                Edit
              </button>
              <button
                onClick={onClose}
                className="text-fg-muted hover:text-fg-secondary"
                type="button"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-primary-dark rounded-full flex items-center justify-center">
                <span className="text-primary-fg font-semibold text-xl">
                  {contact.full_name?.[0] || contact.email?.[0] || '?'}
                </span>
              </div>
              <div className="ml-4 min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold text-fg">{contact.full_name || contact.display_name}</h2>
                  {contact.do_not_contact ? <DncBadge /> : null}
                </div>
                <p className="text-fg-muted">{displayEmail(contact.email) || 'No email'}</p>
                {contact.do_not_contact ? (
                  <div className="rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm space-y-1 max-w-lg">
                    <p className="font-medium text-amber-950 dark:text-amber-50">Suppressed from campaigns</p>
                    {contact.unsubscribed_at ? (
                      <p className="text-xs text-amber-900/90 dark:text-amber-100/85">
                        Unsubscribed: {new Date(contact.unsubscribed_at).toLocaleString()}
                      </p>
                    ) : null}
                    {contact.unsubscribe_source ? (
                      <p className="text-xs text-amber-900/90 dark:text-amber-100/85">
                        Source: {contact.unsubscribe_source}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {contact.stage === 'lead' ? (
              <div className="rounded-xl border border-border p-4 bg-surface-muted/20">
                <h4 className="text-sm font-semibold text-fg mb-3">Opportunity score</h4>
                <ScoreBreakdown
                  score={contact.lead_score ?? contact.score ?? 0}
                  signals={contact.opportunity_signals}
                  expanded
                />
              </div>
            ) : null}

            {contact.capture_type === 'quick_capture' ? (
              <div className="rounded-xl border border-border p-4 space-y-3 bg-surface-muted/30">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-fg">Quick capture</h4>
                  {contact.requires_review ? (
                    <span className="text-xs font-semibold text-warning-fg">Needs review</span>
                  ) : (
                    <span className="text-xs text-fg-muted">Reviewed</span>
                  )}
                </div>
                <AuthenticatedCaptureImage
                  clientId={contact.id}
                  alt="Captured photo"
                  className="max-h-72 w-full object-contain bg-app rounded-lg border border-border"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-fg-muted">
                  {contact.captured_at ? (
                    <span>Captured: {new Date(contact.captured_at).toLocaleString()}</span>
                  ) : null}
                  {contact.captured_location ? <span>Location: {contact.captured_location}</span> : null}
                </div>
                {contact.requires_review ? (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        setActionError('');
                        const updated = await crmService.updateClient(contact.id, { requires_review: false });
                        onContactUpdated(updated);
                        await onRefresh();
                      } catch (e) {
                        setActionError(e instanceof Error ? e.message : 'Failed to update');
                      }
                    }}
                    className="btn-secondary text-sm py-2 px-3 rounded-lg"
                  >
                    Mark as reviewed
                  </button>
                ) : null}
                <p className="text-xs text-fg-muted">
                  Add name, company, and phone using <strong className="text-fg">Edit</strong> when you’re back at
                  your desk.
                </p>
              </div>
            ) : null}

            <div className="rounded-xl border border-border bg-surface-muted/20 p-4">
              <h4 className="text-sm font-semibold text-fg mb-3">Business Identity</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-fg-secondary">Company</label>
                  <p className="text-fg">{contact.company_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-secondary">Website</label>
                  {(() => {
                    const href = getWebsiteLinkHref(contact.website_url);
                    const label = getWebsiteDisplayText(contact.website_url) || 'Not provided';
                    if (!href) return <p className="text-fg-muted">{label}</p>;
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {label}
                      </a>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-secondary">Phone</label>
                  <p className="text-fg">
                    {contact.phone ? formatPhoneDisplay(contact.phone) : 'Not provided'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-secondary">Source</label>
                  <p className="text-fg">{contact.source || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-secondary">Added</label>
                  <p className="text-fg">{new Date(contact.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface-muted/40 p-3 text-sm text-fg">
              <span className="font-medium text-fg-secondary">Next best action: </span>
              {nextBestActionForPipelineClient(contact)}
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Next follow-up</label>
              <input
                type="datetime-local"
                value={nextFollowUpLocal}
                onChange={(e) => setNextFollowUpLocal(e.target.value)}
                onBlur={handleProfileNextFollowUpBlur}
                className="input-field mt-1 w-full max-w-md rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
              />
              <p className="mt-1 text-xs text-fg-muted">Saved when you leave this field. Clear and tab out to remove.</p>
            </div>

            <div className="rounded-xl border border-border p-4">
              <h4 className="text-sm font-semibold text-fg mb-3">Quick Actions</h4>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => handleTouchAction('call')} className="btn-surface-muted px-3 py-1.5 text-sm">Log Call</button>
                <button type="button" onClick={() => { handleTouchAction('email'); onOpenEmail(); }} className="btn-surface-muted px-3 py-1.5 text-sm">Log Email</button>
                <button type="button" onClick={() => handleTouchAction('text')} className="btn-surface-muted px-3 py-1.5 text-sm">Log Text</button>
                <button type="button" onClick={handleScheduleFollowUp} className="btn-surface-muted px-3 py-1.5 text-sm">Schedule Follow-Up</button>
                <button type="button" onClick={onConvert} className="btn-surface-muted px-3 py-1.5 text-sm">Move Stage</button>
                <button type="button" onClick={handleAddNote} className="btn-surface-muted px-3 py-1.5 text-sm">Add Note</button>
                <button type="button" onClick={onOpenEmailSuggestion} className="btn-surface-muted px-3 py-1.5 text-sm">Generate Email Suggestion</button>
                <button type="button" onClick={handleGenerateOutreach} className="btn-surface-muted px-3 py-1.5 text-sm">Generate Outreach Message</button>
              </div>
              {actionError ? <p className="mt-2 text-sm text-danger-fg">{actionError}</p> : null}
              {outreachMessage ? (
                <div className="mt-3 rounded-lg border border-border bg-surface-muted/40 p-3 text-sm text-fg">
                  {outreachMessage}
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-border p-4">
              <h4 className="text-sm font-semibold text-fg mb-2">Reminders (manual)</h4>
              <p className="text-xs text-fg-muted mb-3">
                Separate from email send reminders — use for calls or follow-ups after any outreach.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1 w-full">
                  <label className="text-xs text-fg-muted">Remind at</label>
                  <input
                    type="datetime-local"
                    value={reminderAtDraft}
                    onChange={(e) => setReminderAtDraft(e.target.value)}
                    className="input-field mt-1 w-full rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="text-xs text-fg-muted">Note (optional)</label>
                  <input
                    type="text"
                    value={reminderNoteDraft}
                    onChange={(e) => setReminderNoteDraft(e.target.value)}
                    placeholder="e.g. Send proposal"
                    className="input-field mt-1 w-full rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg"
                  />
                </div>
                <button
                  type="button"
                  disabled={reminderBusy || !reminderAtDraft.trim()}
                  onClick={handleSaveContactReminder}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-fg disabled:opacity-50"
                >
                  {reminderBusy ? 'Saving…' : 'Add reminder'}
                </button>
              </div>
              {contactReminders.length > 0 ? (
                <ul className="mt-3 space-y-2 text-sm">
                  {contactReminders.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
                    >
                      <span className="text-fg">
                        {new Date(r.remind_at).toLocaleString()}
                        {r.note ? <span className="text-fg-muted"> — {r.note}</span> : null}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDismissReminder(r.id)}
                        className="text-xs font-medium text-fg-muted hover:text-fg"
                      >
                        Dismiss
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-fg-muted">No pending reminders for this contact.</p>
              )}
            </div>

            {contact.address && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Address</label>
                <p className="text-fg whitespace-pre-wrap">{contact.address}</p>
              </div>
            )}

            {contact.notes && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Notes</label>
                <p className="text-fg whitespace-pre-wrap">{contact.notes}</p>
              </div>
            )}

            {/* Phase 1: Lead value & upsell summary */}
            <div className="border border-border rounded-xl p-4 bg-surface-muted/30">
              <h4 className="text-sm font-semibold text-fg mb-3">Deal value</h4>
              {loadingUpsells ? (
                <p className="text-sm text-fg-muted">Loading…</p>
              ) : summary ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-fg-muted text-xs">Base lead value</div>
                    <div className="font-semibold text-fg">{formatLeadUpsellUsd(summary.base_lead_value)}</div>
                  </div>
                  <div>
                    <div className="text-fg-muted text-xs">Potential upsell</div>
                    <div className="font-semibold text-fg">{formatLeadUpsellUsd(summary.potential_upsell_value)}</div>
                  </div>
                  <div>
                    <div className="text-fg-muted text-xs">Total potential deal</div>
                    <div className="font-semibold text-primary">{formatLeadUpsellUsd(summary.total_potential_deal_value)}</div>
                  </div>
                  <div>
                    <div className="text-fg-muted text-xs">Won upsell</div>
                    <div className="font-semibold text-fg">{formatLeadUpsellUsd(summary.won_upsell_value)}</div>
                  </div>
                </div>
              ) : null}
              <p className="text-xs text-fg-muted mt-2">
                Set base value on the lead (edit). Upsell rows below add pre-sale expansion before close.
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <h4 className="text-sm font-semibold text-fg">Upsell opportunities</h4>
                {canEditLeadUpsells && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingUpsell(null);
                      setShowUpsellModal(true);
                    }}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-fg hover:bg-primary-dark"
                  >
                    Add upsell
                  </button>
                )}
              </div>
              {upsellError && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{upsellError}</p>}
              {loadingUpsells ? (
                <p className="text-sm text-fg-muted">Loading upsells…</p>
              ) : upsells.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-6 text-center">
                  <p className="text-fg-secondary text-sm">
                    No upsell opportunities yet. Track add-ons, upgrades, or expanded scope to increase deal value
                    before closing.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-surface-muted/50 text-left text-fg-secondary">
                        <th className="px-3 py-2 font-medium">Title</th>
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium text-right">Est. value</th>
                        <th className="px-3 py-2 font-medium">Close</th>
                        {canEditLeadUpsells && <th className="px-3 py-2 font-medium w-24" />}
                      </tr>
                    </thead>
                    <tbody>
                      {upsells.map((u) => (
                        <tr key={u.id} className="border-t border-border">
                          <td className="px-3 py-2 text-fg font-medium">{u.title}</td>
                          <td className="px-3 py-2 text-fg-secondary">{u.upsell_type_display}</td>
                          <td className="px-3 py-2">{u.status_display}</td>
                          <td className="px-3 py-2 text-right">{formatLeadUpsellUsd(u.estimated_value)}</td>
                          <td className="px-3 py-2 text-fg-muted">
                            {u.expected_close_date
                              ? new Date(u.expected_close_date).toLocaleDateString()
                              : '—'}
                          </td>
                          {canEditLeadUpsells && (
                            <td className="px-3 py-2 text-right space-x-2">
                              <button
                                type="button"
                                className="text-primary hover:underline text-xs"
                                onClick={() => {
                                  setEditingUpsell(u);
                                  setShowUpsellModal(true);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-red-600 dark:text-red-400 hover:underline text-xs"
                                onClick={() => handleArchiveUpsell(u)}
                              >
                                Archive
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <ContactOutreachStatus
                client={contact}
                refreshKey={emailRefreshKey}
                onContactUpdated={onContactUpdated}
              />
              <div>
              <h4 className="text-sm font-semibold text-fg mb-2">Email</h4>
              <ContactEmailHistory
                clientId={contact.id}
                refreshKey={emailRefreshKey}
                onLoaded={(n) => setEmailHistoryCount(n)}
                onEditScheduled={onEditScheduled}
              />
              <button
                type="button"
                onClick={onOpenEmail}
                className="mt-3 px-4 py-2 text-sm font-medium text-primary-fg bg-primary hover:bg-primary-dark rounded-xl"
              >
                {emailHistoryCount === null || emailHistoryCount === 0 ? 'Send Email' : 'Send follow-up'}
              </button>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
              <button onClick={onClose} className="px-4 py-2 btn-surface-muted" type="button">
                Close
              </button>
              <button
                onClick={onConvert}
                className="px-4 py-2 text-sm font-medium text-primary-fg bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary rounded-xl"
                type="button"
              >
                Convert to Prospect
              </button>
            </div>
          </div>
        </div>
      </div>

      {showUpsellModal && canEditLeadUpsells && (
        <LeadUpsellFormModal
          key={editingUpsell?.id ?? 'new-upsell'}
          clientId={contact.id}
          initial={editingUpsell}
          onClose={() => {
            setShowUpsellModal(false);
            setEditingUpsell(null);
          }}
          onSaved={async () => {
            setShowUpsellModal(false);
            setEditingUpsell(null);
            await loadUpsells();
          }}
        />
      )}
    </div>
  );
};

interface LeadUpsellFormModalProps {
  clientId: string;
  initial: LeadUpsellOpportunity | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const LeadUpsellFormModal: React.FC<LeadUpsellFormModalProps> = ({ clientId, initial, onClose, onSaved }) => {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [upsellType, setUpsellType] = useState<LeadUpsellType>(initial?.upsell_type ?? 'custom');
  const [estimatedValue, setEstimatedValue] = useState(initial?.estimated_value ?? '0');
  const [status, setStatus] = useState<LeadUpsellStatus>(initial?.status ?? 'identified');
  const [probability, setProbability] = useState<string>(initial?.probability != null ? String(initial.probability) : '');
  const [expectedClose, setExpectedClose] = useState(initial?.expected_close_date?.slice(0, 10) ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    const payload: LeadUpsellWritePayload = {
      title: title.trim(),
      description: description.trim(),
      upsell_type: upsellType,
      estimated_value: estimatedValue,
      status,
      notes: notes.trim(),
    };
    if (probability.trim() !== '') {
      const p = parseInt(probability, 10);
      if (!Number.isNaN(p)) payload.probability = p;
    } else {
      payload.probability = null;
    }
    if (expectedClose) payload.expected_close_date = expectedClose;
    else payload.expected_close_date = null;

    try {
      setSaving(true);
      if (initial) {
        await leadUpsellService.update(clientId, initial.id, payload);
      } else {
        await leadUpsellService.create(clientId, payload);
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop z-[60]">
      <div className="relative top-10 mx-auto p-5 border border-border w-full max-w-lg shadow-lg rounded-2xl bg-surface">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-fg">{initial ? 'Edit upsell' : 'New upsell opportunity'}</h3>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg-secondary">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {err && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{err}</p>}
        <form onSubmit={handleSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <label className="block text-sm font-medium text-fg-secondary mb-1">Title *</label>
            <input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Type</label>
              <select
                value={upsellType}
                onChange={(e) => setUpsellType(e.target.value as LeadUpsellType)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
              >
                {LEAD_UPSELL_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as LeadUpsellStatus)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
              >
                {LEAD_UPSELL_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Est. value ($) *</label>
              <input
                required
                type="number"
                min="0"
                step="0.01"
                value={estimatedValue}
                onChange={(e) => setEstimatedValue(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Probability %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={probability}
                onChange={(e) => setProbability(e.target.value)}
                placeholder="0–100"
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-secondary mb-1">Expected close</label>
            <input
              type="date"
              value={expectedClose}
              onChange={(e) => setExpectedClose(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-secondary mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-fg"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 btn-surface-muted">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-primary-fg font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Leads;









